// import atoms
import { dateRangeAtom, selectedChipAtom } from '../../atoms';

// import components
import { Typography } from '@mui/material';
import { Select } from 'components/shared';
import Chips from './Chips';
import MonthOrWeekRange from './MonthOrWeekRange';

// import enums
import { ChipSelection, SelectedChip } from '../../enum';

// import hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

// import utils
import {
  StyledPeriodSelectorBox,
  StyledPeriodSelectorRangeBox
} from './PeriodSelector.styles';
import { StyledBoxFlexAlignCenter } from 'components/shared/Box/Box.styles';
import {
  getEndOfMonth,
  getLastWeek,
  getNextMonth,
  getNextWeek,
  getStartOfMonth,
  getStartOfWeek,
  getStartOfYear,
  getToday,
  getEndOfWeek,
  getLastMonth
} from './PeriodSelector.utils';

export default function DateSelector() {
  const { t } = useTranslation('common');
  const options = [
    { value: 'month', label: t('month') },
    { value: 'week', label: t('week') }
  ];
  const [selectedPeriodType, setSelectedPeriodType] = useState(
    options[0].value
  );
  const [, setDateRange] = useRecoilState(dateRangeAtom);
  const [selectedChip, setSelectedChip] = useRecoilState(selectedChipAtom);

  const [showDateRange, setShowDateRange] = useState(false);
  const isSetToMonth = selectedPeriodType === 'month';

  const handleChipClick = (chip: SelectedChip) => {
    setSelectedChip(chip);
    setShowDateRange(false);

    switch (chip) {
      case ChipSelection.UNTIL_LAST_MONTH:
        setDateRange([
          getStartOfYear(getLastMonth),
          getEndOfMonth(getLastMonth)
        ]);
        break;
      case ChipSelection.LAST_MONTH:
        setDateRange([
          getStartOfMonth(getLastMonth),
          getEndOfMonth(getLastMonth)
        ]);
        break;
      case ChipSelection.THIS_MONTH:
        setDateRange([getStartOfMonth(getToday), getEndOfMonth(getToday)]);
        break;
      case ChipSelection.NEXT_MONTH:
        setDateRange([
          getStartOfMonth(getNextMonth),
          getEndOfMonth(getNextMonth)
        ]);
        break;
      case ChipSelection.UNTIL_LAST_WEEK:
        setDateRange([getStartOfYear(getLastWeek), getEndOfWeek(getLastWeek)]);
        break;
      case ChipSelection.LAST_WEEK:
        setDateRange([getStartOfWeek(getLastWeek), getEndOfWeek(getLastWeek)]);
        break;
      case ChipSelection.THIS_WEEK:
        setDateRange([getStartOfWeek(getToday), getEndOfWeek(getToday)]);
        break;
      case ChipSelection.NEXT_WEEK:
        setDateRange([getStartOfWeek(getNextWeek), getEndOfWeek(getNextWeek)]);
        break;
      case ChipSelection.CUSTOM:
        setDateRange([null, null]);
        setShowDateRange(true);
        break;
      default:
        return;
    }
  };

  const handleSelect = (val: string) => {
    setSelectedChip('');
    setDateRange([null, null]);
    setShowDateRange(false);

    setSelectedPeriodType(val);
  };

  return (
    <StyledPeriodSelectorBox>
      <StyledPeriodSelectorRangeBox>
        <Typography variant="body1" sx={{ textAlign: 'center' }}>
          {t('adminPortal.realization.selectPeriod')}:
        </Typography>
        <Chips
          isSetToMonth={isSetToMonth}
          selectedChip={selectedChip}
          onChipClick={handleChipClick}
        />
        {showDateRange && (
          <MonthOrWeekRange
            isSetToMonth={isSetToMonth}
            setDateRange={setDateRange}
          />
        )}
      </StyledPeriodSelectorRangeBox>
      <StyledBoxFlexAlignCenter>
        <h4 style={{ marginRight: '16px', flexShrink: 0 }}>
          {t('periodType')}:{' '}
        </h4>
        <Select
          options={options}
          valueProp={selectedPeriodType}
          onSelected={handleSelect}
        />
      </StyledBoxFlexAlignCenter>
    </StyledPeriodSelectorBox>
  );
}
